import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Drawer, Input, Segmented, Select, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { AlertTriangle, Banknote, CircleCheckBig, Landmark, RefreshCw, Search } from 'lucide-react';
import Layout from '../../Layout';
import { ktApi, today, useKt } from './ktStore';
import { checkGroup, POS_CHECK, posPaidDates, posPaidRows, posPaidSummary, type PosGroup, type PosRow } from './ktCounter';
import { billTotal, dmy, fmtMoney, PAY_METHOD, paidTotal } from './ktLogic';
import { DemoBar, Kpi, PageHeader, Panel } from './ktUi';

/** Đơn trên server có thể đổi bất cứ lúc nào: tự đọc lại khi đang mở màn */
const PULL_EVERY_MS = 120_000;
type Filter = 'all' | PosGroup;

const CheckTag = ({ check }: { check: PosRow['check'] }) => <Tag color={POS_CHECK[check].color} className="!mr-0">{POS_CHECK[check].label}</Tag>;

/** Kế toán quầy theo dõi và đối soát đơn POS đã thanh toán thành công. Chỉ đọc: thu tiền, sửa đơn làm trên POS */
export default function KeToanCounter() {
  const s = useKt();
  const now = today();
  const dates = useMemo(() => posPaidDates(s.bills), [s.bills]);
  const [date, setDate] = useState(now);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [pulling, setPulling] = useState(false);
  const busy = useRef(false);

  const pull = async () => {
    if (busy.current) return;
    busy.current = true;
    setPulling(true);
    await ktApi.pullServerOrders();
    busy.current = false;
    setPulling(false);
  };
  useEffect(() => {
    pull();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') pull(); }, PULL_EVERY_MS);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => posPaidRows(s, date), [s, date]);
  const sum = useMemo(() => posPaidSummary(rows), [rows]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => (filter === 'all' || checkGroup(r.check) === filter) && (!q || [r.bill.customer, r.bill.phone, r.bill.posOrderId, r.bill.no, ...r.bill.payments.map(p => p.ref)].some(x => (x || '').toLowerCase().includes(q))));
  }, [rows, filter, query]);
  const others = useMemo(() => {
    const posOfDay = s.bills.filter(b => b.posOrderId && b.date === date);
    return { debt: posOfDay.filter(b => b.status === 'valid' && b.debt > 0).length, cancelled: posOfDay.filter(b => b.status === 'cancelled').length };
  }, [s.bills, date]);
  const detail = rows.find(r => r.bill.id === detailId) || null;
  const latest = dates.find(d => d !== date);

  const columns: ColumnsType<PosRow> = [
    { title: 'Giờ', key: 'at', width: 70, render: (_, r) => dayjs(r.bill.at).format('HH:mm') },
    { title: 'Đơn POS', key: 'no', width: 145, render: (_, r) => <div><div className="font-medium text-gray-900 dark:text-gray-100">{r.bill.posOrderId}</div><div className="text-[11px] text-gray-500">{r.bill.no}</div></div> },
    { title: 'Khách', key: 'c', width: 150, render: (_, r) => <div><div className="text-gray-900 dark:text-gray-100">{r.bill.customer}</div><div className="text-[11px] text-gray-500">{r.bill.phone || 'Không có số điện thoại'}</div></div> },
    { title: 'Dịch vụ', key: 'l', render: (_, r) => (
      <Tooltip title={r.bill.lines.length > 1 ? r.bill.lines.map(l => l.name).join(', ') : undefined}>
        <div className="min-w-0">
          <div className="text-xs text-gray-700 dark:text-gray-300 line-clamp-1">{r.bill.lines[0]?.name}</div>
          {r.bill.lines.length > 1 && <div className="text-[11px] text-gray-500">và {r.bill.lines.length - 1} dịch vụ khác</div>}
        </div>
      </Tooltip>
    ) },
    { title: 'Đã thu', key: 'm', width: 130, align: 'right', render: (_, r) => (
      <div>
        <b>{fmtMoney(r.paid)}</b>
        {r.refunded > 0 && <div className="text-[11px] text-rose-600">Đã hoàn {fmtMoney(r.refunded)}</div>}
      </div>
    ) },
    { title: 'Hình thức, mã giao dịch', key: 'p', width: 200, render: (_, r) => r.bill.payments.map(p => (
      <div key={p.id} className="text-xs">
        <span className="text-gray-700 dark:text-gray-300">{PAY_METHOD[p.method]}{r.bill.payments.length > 1 ? ` ${fmtMoney(p.amount)}` : ''}</span>
        {p.ref && <span className="text-gray-400"> · {p.ref}</span>}
      </div>
    )) },
    { title: 'Thu ngân', key: 'by', width: 100, render: (_, r) => <span className="text-xs">{r.bill.createdBy}</span> },
    { title: 'Đối soát', key: 's', width: 150, render: (_, r) => <CheckTag check={r.check} /> },
    { title: '', key: 'a', width: 70, align: 'right', render: (_, r) => <Button size="small" aria-label={`Xem ${r.bill.posOrderId}`} onClick={() => setDetailId(r.bill.id)}>Xem</Button> },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Đối soát POS" desc="Đơn đã thanh toán thành công trên POS. Màn này chỉ để theo dõi và đối soát, thu tiền hoặc sửa đơn làm trên POS."
          extra={<Select aria-label="Chọn ngày" value={date} onChange={setDate} className="w-56" options={[now, ...dates.filter(d => d !== now)].map(d => ({ value: d, label: `${dmy(d)}${d === now ? ' (hôm nay)' : ''}${s.locks[d] ? ' · đã khóa' : ''}` }))} />} />
        <DemoBar portal="counter" />
        {s.posSync.serverError && <Alert type="error" showIcon message={`Không đọc được đơn POS trên server: ${s.posSync.serverError}`} />}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<CircleCheckBig size={20} />} tone="bg-emerald-50 text-emerald-600" label="Đã thu thành công" value={fmtMoney(sum.total)} hint={`${rows.length} đơn POS`} />
          <Kpi icon={<Banknote size={20} />} tone="bg-sky-50 text-sky-600" label="Tiền mặt" value={fmtMoney(sum.cash)} hint="Đếm và đối chiếu khi chốt ca" />
          <Kpi icon={<Landmark size={20} />} tone="bg-violet-50 text-violet-600" label="Ngân hàng, Payoo" value={fmtMoney(sum.nonCash)} hint={sum.nonCashCount ? `Đã khớp ${sum.matchedCount}/${sum.nonCashCount} giao dịch` : 'Không có giao dịch'} />
          <Kpi icon={<AlertTriangle size={20} />} tone="bg-rose-50 text-rose-600" label="Cần kiểm tra" value={sum.attention} hint="Thiếu mã, trùng mã, lệch tiền" />
        </div>

        <Panel title={`Đơn đã thanh toán ngày ${dmy(date)} (${shown.length})`}
          extra={(
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{s.posSync.serverAt ? `Đọc từ server lúc ${dayjs(s.posSync.serverAt).format('HH:mm')}` : 'Chưa đọc từ server'}</span>
              <Button size="small" loading={pulling} icon={<RefreshCw size={13} />} onClick={pull}>Đồng bộ</Button>
            </div>
          )}>
          <div className="px-4 pt-3 pb-1 flex flex-col lg:flex-row lg:items-center justify-between gap-2">
            <Input aria-label="Tìm đơn" allowClear className="lg:max-w-sm" prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm theo khách, số điện thoại, mã đơn, mã giao dịch" value={query} onChange={e => setQuery(e.target.value)} />
            <Segmented<Filter> value={filter} onChange={setFilter} options={[
              { value: 'all', label: `Tất cả (${rows.length})` },
              { value: 'attention', label: `Cần kiểm tra (${sum.attention})` },
              { value: 'waiting', label: `Chờ tiền về (${sum.waiting})` },
              { value: 'done', label: `Xong (${sum.done})` },
            ]} />
          </div>
          {(others.debt > 0 || others.cancelled > 0) && (
            <p className="px-4 pt-2 m-0 text-xs text-gray-500">
              Ngày này còn {[others.debt && `${others.debt} đơn chưa thu đủ`, others.cancelled && `${others.cancelled} đơn đã hủy`].filter(Boolean).join(' và ')}, không nằm trong danh sách. Xem ở <Link to="/ke-toan/counter-bills" className="text-[#3e91ab] hover:underline">Bill trong ngày</Link>.
            </p>
          )}
          <div className="p-4 pt-3">
            <Table rowKey={r => r.bill.id} size="small" columns={columns} dataSource={shown} pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: 1100 }}
              locale={{ emptyText: (
                <div className="py-6 text-sm text-gray-500 space-y-2">
                  <div>{rows.length ? 'Không có đơn khớp với bộ lọc' : `Ngày ${dmy(date)} chưa có đơn POS nào thanh toán thành công`}</div>
                  {!rows.length && latest && <Button size="small" onClick={() => setDate(latest)}>Xem ngày gần nhất có đơn: {dmy(latest)}</Button>}
                </div>
              ) }} />
          </div>
        </Panel>
      </div>

      <Drawer open={!!detail} onClose={() => setDetailId(null)} title={detail ? `${detail.bill.posOrderId} · ${detail.bill.customer}` : ''} width={560}>
        {detail && <OrderDetail row={detail} />}
      </Drawer>
    </Layout>
  );
}

function OrderDetail({ row }: { row: PosRow }) {
  const b = row.bill;
  return (
    <div className="space-y-5 text-sm">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="text-gray-500">Số bill kế toán</div><div>{b.no}</div>
        <div className="text-gray-500">Thời gian</div><div>{dayjs(b.at).format('HH:mm DD/MM/YYYY')}</div>
        <div className="text-gray-500">Khách, điện thoại</div><div>{b.customer}{b.phone ? `, ${b.phone}` : ''}</div>
        <div className="text-gray-500">Thu ngân</div><div>{b.createdBy}</div>
        <div className="text-gray-500">Tiền bill, đã thu</div><div>{fmtMoney(billTotal(b))}, {fmtMoney(paidTotal(b))}</div>
      </div>

      <div>
        <div className="font-medium mb-1">Dịch vụ</div>
        <Table size="small" pagination={false} rowKey="id" dataSource={b.lines} columns={[
          { title: 'Tên', dataIndex: 'name' },
          { title: 'Giá', key: 'l', align: 'right', render: (_, l) => fmtMoney(l.list ?? l.gross) },
          { title: 'Chiết khấu', key: 'd', align: 'right', render: (_, l) => fmtMoney((l.lineDiscount || 0) + (l.orderDiscount || 0)) },
          { title: 'Thành tiền', key: 'n', align: 'right', render: (_, l) => <b>{fmtMoney(l.gross)}</b> },
        ]} />
      </div>

      <div>
        <div className="font-medium mb-1">Giao dịch</div>
        <Table size="small" pagination={false} rowKey={x => x.payment.id} dataSource={row.pays} columns={[
          { title: 'Hình thức', key: 'm', render: (_, x) => PAY_METHOD[x.payment.method] },
          { title: 'Số tiền', key: 'a', align: 'right', render: (_, x) => fmtMoney(x.payment.amount) },
          { title: 'Mã', key: 'r', render: (_, x) => x.payment.ref || <span className="text-gray-400">Không có</span> },
          { title: 'Đối soát', key: 's', render: (_, x) => <CheckTag check={x.check} /> },
        ]} />
      </div>

      {b.refunds.length > 0 && (
        <div>
          <div className="font-medium mb-1">Hoàn tiền trên POS</div>
          <div className="space-y-1 text-xs">
            {b.refunds.map(r => <div key={r.id} className="flex justify-between gap-2"><span>{dmy(r.date)} · {PAY_METHOD[r.method]}{r.ref ? ` · ${r.ref}` : ''}</span><b>{fmtMoney(r.amount)}</b></div>)}
          </div>
        </div>
      )}
      <p className="text-xs text-gray-500 m-0">Màn này chỉ xem. Đơn sai thì xử lý trên POS, kế toán tự nhận thay đổi.</p>
    </div>
  );
}
